// @ts-check

// If you're adding a new feature, you must add it to
// app/src/scripts/utils/ubpUtils.js so it shows up in the UI
import { OPENAI_PRICING, getConversionRateForModel } from "../openAiProxy/openAiPricing";
import { getEmailConversionRate } from "../emailProxy/emailPricing";
import {
    getSmsConversionRate,
    getMmsConversionRate,
    getSmsInternationalConversionRate,
    getMmsInternationalConversionRate
} from "../twilioProxy/twilioPricing";
import { getGoogleVisionOCRConversionRate } from "../googleVisionProxy/googleVisionPricing";
import { GEMINI_PRICING, getConversionRateForGeminiModel } from "../geminiProxy/geminiPricing";
import {
    ANTHROPIC_PRICING,
    getConversionRateForAnthropicModel
} from "../anthropicProxy/anthropicPricing";
import { WEBRTC_PLAN_RATES } from "../webrtc/webrtcService";

const _DEFAULT_CONVERSION_RATES = {
    // unit: 1 image
    // 1 credit = 100 images
    "ai-labeling": 1 / 100,

    // unit: 1 inference
    // 1 credit = 1000 inferences
    "hostedInference": 1 / 1000,

    // unit: 1 train second
    // 1 credit = 30 minutes
    "train": 1 / 30 / 60,
    //"train_rfdetr": 1 / 30 / 60 / 2,

    // unit: 1 image / percentage of month
    // 1 credit = 5000 monthly images
    "image-source-storage": 1 / 5000,

    // unit: 1 image / percentage of month
    // 1 credit = 5000 monthly images
    "image-transform-storage": 1 / 5000,

    // unit: 1 image
    // 1 credit = 20000 images
    "image-generation": 1 / 20000,

    // unit: 1 image
    // 1 credit = 10000 images indexed
    "image-indexing": 1 / 10000,

    // unit: 1 hour x 1 FPS (inference FPS not video FPS)
    // 1 credit = 200 minutes per FPS
    "batch-video-inference": 1 / (200 / 60),

    // unit: 1 hour
    // 1 credit = 4 hour
    "dedicated-machine-hourly_dev-cpu": 1 / 4,

    // unit: 1 hour
    // 1 credit = 1 hour
    "dedicated-machine-hourly_dev-gpu": 1,

    // unit: 1 hour
    // 1 credit = 4 hour
    "dedicated-machine-hourly_prod-cpu": 1 / 4,

    // unit: 1 hour
    // 1 credit = 1 hour
    "dedicated-machine-hourly_prod-gpu": 1,

    // unit: 1 second
    // 1 credit = 500 minutes
    "self-hosted-stream": 1 / 500 / 60,

    // unit: 1 run
    // 1 credit = 3,000 inference runs (workflows or model)
    // equivalent of 48 hours of live video at 1 frame per minute
    "self-hosted-inference-run": 1 / 3000,

    // unit: 1 second
    // 4 credits = 1 GPU / hr
    "batch-processing-compute-gpu": 4 / 3600,

    // unit: 1 second
    // 1 credits = 1 CPU / hr
    "batch-processing-compute-cpu": 1 / 3600,

    // unit: 1 second
    // 1 credits = 500 seconds
    "serverless-inference-run": 1 / 500
};

for (const model of Object.keys(OPENAI_PRICING)) {
    const inputRate = getConversionRateForModel(model, "input");
    const outputRate = getConversionRateForModel(model, "output");
    if (inputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--open-ai-${model}-input`] = inputRate;
    }
    if (outputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--open-ai-${model}-output`] = outputRate;
    }
}

// Email notification pricing
_DEFAULT_CONVERSION_RATES["third-party-api--email-notification"] = getEmailConversionRate();

// Twilio SMS/MMS notification pricing
// Base rates (US/Canada - used as default fallback)
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-sms"] = getSmsConversionRate();
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-mms"] = getMmsConversionRate();
// Country-parameterized rates - US/Canada (30 messages per credit)
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-sms_us-ca"] = getSmsConversionRate();
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-mms_us-ca"] = getMmsConversionRate();
// Country-parameterized rates - International (10 messages per credit)
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-sms_intl"] = getSmsInternationalConversionRate();
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-mms_intl"] = getMmsInternationalConversionRate();

// Google Vision OCR pricing
_DEFAULT_CONVERSION_RATES["third-party-api--google-vision-ocr"] =
    getGoogleVisionOCRConversionRate();

// WebRTC plan pricing (rates are credits per hour, convert to credits per second)
for (const [plan, creditsPerHour] of Object.entries(WEBRTC_PLAN_RATES)) {
    _DEFAULT_CONVERSION_RATES[plan] = creditsPerHour / 3600;
}

// Gemini API pricing (token-based, like OpenAI)
for (const model of Object.keys(GEMINI_PRICING)) {
    const inputRate = getConversionRateForGeminiModel(model, "input");
    const outputRate = getConversionRateForGeminiModel(model, "output");
    if (inputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--gemini-${model}-input`] = inputRate;
    }
    if (outputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--gemini-${model}-output`] = outputRate;
    }
}

// Anthropic API pricing (token-based, like OpenAI)
for (const model of Object.keys(ANTHROPIC_PRICING)) {
    const inputRate = getConversionRateForAnthropicModel(model, "input");
    const outputRate = getConversionRateForAnthropicModel(model, "output");
    if (inputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--anthropic-${model}-input`] = inputRate;
    }
    if (outputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--anthropic-${model}-output`] = outputRate;
    }
}

/**
 * Returns all possible feature combinations from a feature string, splitting on underscores
 * @param {string} feature - Feature string with parts separated by underscores
 * @returns {string[]} Array of feature combinations, from most specific to base
 * @example
 * // Returns ['dedicated-machine-hourly_prod-gpu_us-1', 'dedicated-machine-hourly_prod-gpu', 'dedicated-machine-hourly']
 * getFeatureComponents('dedicated-machine-hourly_prod-gpu_us-1')
 */
function getFeatureComponents(feature) {
    const parts = feature.split("_");
    if (parts.length === 1) {
        return [feature];
    }

    const results = [feature];
    while (parts.length > 1) {
        parts.pop();
        results.push(parts.join("_"));
    }
    return results;
}

function featureConversionRate({ usage, feature }) {
    const featureComponents = getFeatureComponents(feature);

    for (const featureComponent of featureComponents) {
        if (
            usage?.creditConversionRate?.[featureComponent] &&
            typeof usage?.creditConversionRate?.[featureComponent] === "number"
        ) {
            return usage?.creditConversionRate?.[featureComponent];
        }

        if (_DEFAULT_CONVERSION_RATES[featureComponent]) {
            return _DEFAULT_CONVERSION_RATES[featureComponent];
        }
    }

    throw new Error(`Unknown feature ${feature} not in conversion rates`);
}

export { featureConversionRate, _DEFAULT_CONVERSION_RATES, getFeatureComponents };
export default { featureConversionRate, _DEFAULT_CONVERSION_RATES, getFeatureComponents };
